/**
 * Emission factor data for the methodology modal.
 * Updated January 2026 based on DEFRA/DESNZ 2025 Greenhouse Gas Conversion Factors.
 *
 * These values MUST match the factors used in the actual calculator code:
 *   - calculate-movement.ts
 *   - calculate-into-city-from-airport-movement.ts
 *   - calculate-around-city-movement.ts
 *   - calculate-accomodation.ts
 */

export type EmissionFactorEntry = {
  modeKey: string;
  factor: number;
  unit: string;
  source: string;
  sourceUrl?: string;
};

export type ClassMultiplierEntry = {
  classKey: string;
  multiplier: number;
};

export type AccommodationCoefficientEntry = {
  typeKey: string;
  coefficient: number;
};

export type BenchmarkEntry = {
  levelKey: string;
  threshold: string;
  color: string;
};

export type SourceEntry = {
  id: number;
  nameKey: string;
  url: string;
};

// ─── Mobility Emission Factors ───

export const MOBILITY_EMISSION_FACTORS: EmissionFactorEntry[] = [
  {
    modeKey: 'ship',
    factor: 0.11286,
    unit: 'kg CO₂e/pkm',
    source: 'DEFRA 2025',
    sourceUrl: 'https://www.gov.uk/government/publications/greenhouse-gas-reporting-conversion-factors-2025',
  },
  {
    modeKey: 'train',
    factor: 0.03546,
    unit: 'kg CO₂e/pkm',
    source: 'DEFRA 2025',
    sourceUrl: 'https://www.gov.uk/government/publications/greenhouse-gas-reporting-conversion-factors-2025',
  },
  {
    modeKey: 'airplane',
    factor: 0.10916,
    unit: 'kg CO₂e/pkm',
    source: 'DEFRA 2025',
    sourceUrl: 'https://www.gov.uk/government/publications/greenhouse-gas-reporting-conversion-factors-2025',
  },
  {
    modeKey: 'car',
    factor: 0.16272,
    unit: 'kg CO₂e/pkm',
    source: 'DEFRA 2025',
    sourceUrl: 'https://www.gov.uk/government/publications/greenhouse-gas-reporting-conversion-factors-2025',
  },
  {
    modeKey: 'electricCar',
    factor: 0.04047,
    unit: 'kg CO₂e/pkm',
    source: 'DEFRA 2025',
    sourceUrl: 'https://www.gov.uk/government/publications/greenhouse-gas-reporting-conversion-factors-2025',
  },
  {
    modeKey: 'bus',
    factor: 0.10385,
    unit: 'kg CO₂e/pkm',
    source: 'DEFRA 2025',
    sourceUrl: 'https://www.gov.uk/government/publications/greenhouse-gas-reporting-conversion-factors-2025',
  },
  {
    modeKey: 'helicopter',
    factor: 9.8394,
    unit: 'kg CO₂e/pkm',
    source: 'BAZL',
    sourceUrl: 'https://www.bazl.admin.ch',
  },
  {
    modeKey: 'cycleWalking',
    factor: 0,
    unit: 'kg CO₂e/pkm',
    source: '',
  },
];

// ─── Flight Per-Class Emission Factors ───
// DEFRA 2025 non-UK international factors (with RF, 8% distance uplift)
// First Class derived via IATA RP 1726 ratio (4× economy)
// Private Jet derived via industry ratio (8× economy)

export const FLIGHT_CLASS_FACTORS: { classKey: string; factor: number; source: string }[] = [
  { classKey: 'economy', factor: 0.10916, source: 'DEFRA 2025' },
  { classKey: 'business', factor: 0.31656, source: 'DEFRA 2025' },
  { classKey: 'firstClass', factor: 0.43664, source: 'IATA RP 1726' },
  { classKey: 'privateJet', factor: 0.87328, source: 'Original methodology' },
];

// Retained for display — shows the IATA-standard multiplier relative to economy
export const CLASS_MULTIPLIERS: ClassMultiplierEntry[] = [
  { classKey: 'economy', multiplier: 1 },
  { classKey: 'business', multiplier: 2.9 },
  { classKey: 'firstClass', multiplier: 4.0 },
  { classKey: 'privateJet', multiplier: 8.0 },
];

// ─── Airport Transfer Factors (NCE → Monaco) ───

export const AIRPORT_TRANSFER_FACTORS: EmissionFactorEntry[] = [
  {
    modeKey: 'bus',
    factor: 0.10385,
    unit: 'kg CO₂e/pkm',
    source: 'DEFRA 2025',
  },
  {
    modeKey: 'taxi',
    factor: 0.14549,
    unit: 'kg CO₂e/pkm',
    source: 'DEFRA 2025',
  },
  {
    modeKey: 'train',
    factor: 0.03546,
    unit: 'kg CO₂e/pkm',
    source: 'DEFRA 2025',
  },
  {
    modeKey: 'helicopter',
    factor: 9.8394,
    unit: 'kg CO₂e/pkm',
    source: 'BAZL',
  },
];

export const AIRPORT_DISTANCES = {
  roadKm: 31.5,
  trainKm: 18,
};

// ─── Accommodation Factors ───

export const ACCOMMODATION_BASE_FACTOR = 8.0; // kg CO₂e per person per night

export const ACCOMMODATION_COEFFICIENTS: AccommodationCoefficientEntry[] = [
  { typeKey: 'hotel2Star', coefficient: 0.33 },
  { typeKey: 'hotel3Star', coefficient: 0.5 },
  { typeKey: 'hotel4Star', coefficient: 1.14 },
  { typeKey: 'hotel5Star', coefficient: 1.86 },
  { typeKey: 'privateAccommodation', coefficient: 0.2 },
];

// ─── Benchmarking Thresholds ───

export const BENCHMARKING_THRESHOLDS: BenchmarkEntry[] = [
  { levelKey: 'low', threshold: '≤ 0.3', color: 'var(--color13)' },
  { levelKey: 'average', threshold: '0.3 – 1.5', color: 'var(--color10)' },
  { levelKey: 'high', threshold: '≥ 1.5', color: 'var(--color2)' },
];

// ─── Source References ───

export const SOURCE_REFERENCES: SourceEntry[] = [
  {
    id: 1,
    nameKey: 'sncf',
    url: 'https://www.sncf-connect.com/en-ch/help/calculation-co2-emissions-your-train-journey',
  },
  {
    id: 2,
    nameKey: 'defra',
    url: 'https://www.gov.uk/government/publications/greenhouse-gas-reporting-conversion-factors-2025',
  },
  {
    id: 3,
    nameKey: 'iata',
    url: 'https://iata.org/en/programs/sustainability/ecohub/passenger-emissions-methodology',
  },
  {
    id: 4,
    nameKey: 'ademe',
    url: 'https://data.ademe.fr/datasets/base-carbone',
  },
  {
    id: 5,
    nameKey: 'bazl',
    url: 'https://www.bazl.admin.ch',
  },
  {
    id: 6,
    nameKey: 'cornell',
    url: 'https://www.hotelfootprints.org',
  },
];
